/**
 * WorkspaceDetailsOverlay — floating overlay showing workspace metadata.
 *
 * Opened by clicking the top-left validation status marker. Shows the
 * PLANNER-AUTHORED output requirement for this specific workspace
 * (the plan node's description + incoming edges' handoff notes), plus
 * the build-workspace requirements checklist when applicable. NOT a
 * slide-in; floats over the workspace content so it doesn't compete
 * with node-details or findings drawers.
 *
 * Content source of truth:
 * - Plan-node `content` = project-specific description of what this
 *   workspace produces (rendered verbatim).
 * - Incoming edges' `output_spec` = handoff notes from upstream.
 * - For build workspaces, the parsed [REQ-NNN] checklist from the
 *   upstream research workspace's model-requirements doc.
 *
 * The overlay does NOT fetch a generic deliverable-shape template —
 * the planner's description IS the project's clarity.
 *
 * Refs #89
 */

import React, { useEffect, useState } from 'react';
import { CornerDownRight, X } from 'lucide-react';
import api, { authHeaders, SpecResolutionError } from '../../apiService';
import DialogShell from '../ui/DialogShell';

export type WorkspaceDetailsType = 'research' | 'production' | 'institution_context';

export interface RequirementItem {
  req_id: string;
  text: string;
  done: boolean;
  implementation_note: string;
  section: string;
  line_index: number;
}

interface IncomingEdgeSpec {
  from_node_id: string;
  from_label: string;
  output_spec: string;
}

interface PlanNodeSummary {
  label: string;             // deliverable label, e.g. "Model spec"
  title: string;             // the plan-node display title
  content: string;           // planner's project-specific description
  incoming_specs: IncomingEdgeSpec[];
}

interface WorkspaceDetailsOverlayProps {
  /** Current workspace type — drives which sections are rendered. */
  workspaceType: WorkspaceDetailsType;
  /** Current project id, used for fetching the plan graph + requirements. */
  projectId: string;
  /** Current workspace id, matched against plan nodes' workspace_id. */
  workspaceId: string;
  /** Current validation state for the badge at the top of the overlay. */
  validationLabel?: string;
  /** Auth token for API calls. */
  token: string;
  /** Report Library URL for plan-graph lookup. */
  reportLibraryUrl?: string;
  /** User id for Report Library ownership check. */
  userId?: string;
  /** Close the overlay. */
  onClose: () => void;
  /** Render a ready flag from parent so overlay refreshes on updates. */
  refreshSignal?: number;
}

export default function WorkspaceDetailsOverlay({
  workspaceType,
  projectId,
  workspaceId,
  validationLabel,
  token,
  reportLibraryUrl,
  userId,
  onClose,
  refreshSignal,
}: WorkspaceDetailsOverlayProps) {
  const titleId = React.useId();
  const [plannerSummary, setPlannerSummary] = useState<PlanNodeSummary | null>(null);
  const [requirements, setRequirements] = useState<{
    items: RequirementItem[];
    sections: string[];
    total: number;
    done: number;
    seeded: boolean;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch plan-node description for this workspace + (build only) requirements
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const loaders: Array<Promise<void>> = [];

        if (reportLibraryUrl) {
          loaders.push(
            fetchPlannerSummary(reportLibraryUrl, projectId, workspaceId, userId || '')
              .then((s) => {
                if (!cancelled) setPlannerSummary(s);
              })
              .catch(() => { /* planner summary is optional */ })
          );
        }

        if (workspaceType === 'production') {
          loaders.push(
            api.getWorkspaceRequirements(projectId, workspaceId, token).then((r) => {
              if (!cancelled) {
                setRequirements({
                  items: r.items,
                  sections: r.sections,
                  total: r.total,
                  done: r.done,
                  seeded: r.seeded,
                });
              }
            }).catch((e: unknown) => {
              if (cancelled) return;
              // Say WHAT failed and whether retrying helps. A bare status here
              // left the overlay showing its "no requirements" shape under a
              // generic banner — two different problems reading as one.
              const detail = e instanceof SpecResolutionError
                ? [
                    e.message,
                    e.reason,
                    e.workspaces.length ? `Could not read: ${e.workspaces.join(', ')}` : '',
                    e.retriable
                      ? 'This is transient — reopen this panel to try again.'
                      : 'Retrying will not help — this needs an operator.',
                  ].filter(Boolean).join(' ')
                : (e instanceof Error ? e.message : String(e));
              setError(detail);
              setRequirements(null);
            })
          );
        } else {
          setRequirements(null);
        }

        await Promise.all(loaders);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [workspaceType, projectId, workspaceId, token, reportLibraryUrl, userId, refreshSignal]);

  const handleRevalidate = () => {
    setValidating(true);
    const ok = api.sendValidateCommand('full');
    if (!ok) {
      setError('No active session — send a chat message to start, then try again.');
    }
    // Validation runs in the background and emits WORKSPACE_UPDATE events;
    // the button returns to idle when the parent refreshes us.
    setTimeout(() => setValidating(false), 1500);
  };

  const workspaceTitle = {
    research: 'Research workspace',
    production: 'Build workspace',
    institution_context: 'Institution context workspace',
  }[workspaceType];

  const deliverableLabel = plannerSummary?.label || '';

  return (
    <DialogShell
      open
      onClose={onClose}
      labelledBy={titleId}
      backdropPolicy="dismiss"
      overlayClassName="flex items-start justify-center p-4"
      panelClassName="w-full max-w-3xl max-h-[85vh] mt-16 flex flex-col overflow-hidden"
    >
        <div className="flex items-center justify-between p-4 border-b border-border-default">
          <div>
            <h3 id={titleId} className="text-lg font-semibold text-fg">{workspaceTitle} details</h3>
            <div className="mt-1 text-xs text-fg-tertiary flex items-center gap-3 flex-wrap">
              {plannerSummary?.title && (
                <span>
                  Component: <span className="font-medium text-fg-secondary">{plannerSummary.title}</span>
                </span>
              )}
              {deliverableLabel && (
                <span>
                  Deliverable: <span className="font-medium text-fg-secondary">{deliverableLabel}</span>
                </span>
              )}
              {validationLabel && (
                <span>
                  Validation: <span className="font-medium text-fg-secondary">{validationLabel}</span>
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRevalidate}
              disabled={validating}
              className="px-3 py-1.5 text-sm font-medium rounded-md border border-border-default bg-surface hover:bg-bg-warm disabled:opacity-50"
            >
              {validating ? 'Validating…' : 'Re-validate'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded-full text-fg-tertiary hover:bg-bg-warm"
              aria-label="Close"
            >
              <X size={20} strokeWidth={1.5} aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {error && (
            <div className="p-3 rounded-md bg-danger-bg border border-danger text-sm text-danger">
              {error}
            </div>
          )}

          {loading && !plannerSummary && !requirements && (
            <p className="text-sm text-fg-tertiary">Loading workspace details…</p>
          )}

          {plannerSummary && (plannerSummary.content || plannerSummary.incoming_specs.length > 0) && (
            <PlannerRequirementBlock summary={plannerSummary} />
          )}

          {workspaceType === 'production' && (
            <RequirementsSection
              loading={loading}
              requirements={requirements}
            />
          )}

          {!plannerSummary && !requirements && !loading && !error && (
            <p className="text-sm text-fg-tertiary">
              No workspace details available. The plan node may not have a
              description yet, or this workspace has not been linked to the
              plan.
            </p>
          )}
        </div>
    </DialogShell>
  );
}

// ---------------------------------------------------------------------------
// Planner requirement (all spawned workspace types)
// ---------------------------------------------------------------------------

function PlannerRequirementBlock({ summary }: { summary: PlanNodeSummary }) {
  return (
    <div>
      <h4 className="text-sm font-semibold text-fg-secondary mb-1">
        What this workspace must produce
      </h4>
      <p className="text-xs text-fg-tertiary mb-3">
        Authored by the planning agent for this project. Rendered here as the
        output requirement — the agent works against this text, the validator
        checks against this text.
      </p>
      {summary.content ? (
        <div className="text-sm text-fg whitespace-pre-wrap bg-bg-warm rounded-md p-3 border border-border-default">
          {summary.content}
        </div>
      ) : (
        <p className="text-sm text-fg-tertiary italic">
          (no description written — ask the planner to fill it in)
        </p>
      )}
      {summary.incoming_specs.length > 0 && (
        <div className="mt-4">
          <h5 className="text-sm font-semibold text-fg-secondary mb-2">
            Inputs from upstream
          </h5>
          <ul className="space-y-2">
            {summary.incoming_specs.map((spec, i) => (
              <li key={`${spec.from_node_id}-${i}`} className="text-sm">
                <span className="font-medium text-fg-secondary">
                  {spec.from_label || spec.from_node_id}
                </span>
                {spec.output_spec ? (
                  <span className="text-fg-secondary"> — {spec.output_spec}</span>
                ) : (
                  <span className="text-fg-tertiary italic"> — no handoff note</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Requirements section (build workspaces only)
// ---------------------------------------------------------------------------

function RequirementsSection({
  loading,
  requirements,
}: {
  loading: boolean;
  requirements: {
    items: RequirementItem[];
    sections: string[];
    total: number;
    done: number;
    seeded: boolean;
  } | null;
}) {
  if (loading) {
    return <p className="text-sm text-fg-tertiary">Loading requirements…</p>;
  }
  if (!requirements || !requirements.seeded || requirements.total === 0) {
    return (
      <div>
        <h4 className="text-sm font-semibold text-fg-secondary mb-2">Model requirements</h4>
        <p className="text-sm text-fg-tertiary">
          No requirements imported yet. They are seeded automatically the first
          time the build agent marks a requirement as done, using the upstream
          research workspace's report.
        </p>
      </div>
    );
  }

  // Group by section while preserving order of first appearance
  const grouped: Array<{ section: string; items: RequirementItem[] }> = [];
  const seen = new Map<string, number>();
  for (const it of requirements.items) {
    const key = it.section || '(unsectioned)';
    if (!seen.has(key)) {
      seen.set(key, grouped.length);
      grouped.push({ section: key, items: [] });
    }
    grouped[seen.get(key)!].items.push(it);
  }

  return (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <h4 className="text-sm font-semibold text-fg-secondary">Model requirements</h4>
        <span className="text-xs text-fg-tertiary">
          {requirements.done}/{requirements.total} implemented
        </span>
      </div>
      <div className="space-y-4">
        {grouped.map(({ section, items }) => (
          <div key={section}>
            <div className="text-xs uppercase tracking-wide text-fg-tertiary mb-1">{section}</div>
            <ul className="space-y-1.5">
              {items.map((r) => (
                <li key={r.req_id} className="text-sm">
                  <span className="inline-flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={r.done}
                      readOnly
                      className="mt-0.5 accent-green-600"
                      aria-label={r.done ? 'done' : 'todo'}
                    />
                    <span className="min-w-0">
                      <span className="font-mono text-xs text-fg-tertiary mr-1">
                        [{r.req_id}]
                      </span>
                      <span className={r.done ? 'text-fg-tertiary line-through' : 'text-fg'}>
                        {r.text}
                      </span>
                      {r.implementation_note && (
                        <span className="flex items-start gap-1 text-xs text-fg-tertiary mt-0.5 ml-1">
                          <CornerDownRight size={16} strokeWidth={1.5} className="flex-shrink-0" aria-hidden="true" />
                          <span>{r.implementation_note}</span>
                        </span>
                      )}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Plan-graph lookup (direct call to report_library)
// ---------------------------------------------------------------------------

async function fetchPlannerSummary(
  reportLibraryUrl: string,
  projectId: string,
  workspaceId: string,
  userId: string,
): Promise<PlanNodeSummary | null> {
  const planWsId = `ws_${projectId}`;
  const url = `${reportLibraryUrl.replace(/\/$/, '')}/workspaces/${planWsId}/state?user_id=${encodeURIComponent(userId)}`;
  // BAR-750: auth-gated endpoint — tokenless 401s under ENFORCE_AUTH and the
  // `return null` below hides it, leaving the details overlay silently empty.
  const resp = await fetch(url, { headers: await authHeaders() });
  if (!resp.ok) return null;
  const data = await resp.json();
  const nodes: Record<string, any> = data.nodes || {};
  const edgesRaw: any = data.edges || {};
  const edgeMetadata: Record<string, Record<string, any>> = data.edge_metadata || {};
  const edges: Array<{ fromNodeId: string; toNodeId: string; outputSpec: string }> = [];
  if (Array.isArray(edgesRaw)) {
    for (const e of edgesRaw) {
      const legacy = e as Record<string, any>;
      const fromId = legacy.from_node_id || '';
      const toId = legacy.to_node_id || '';
      if (!fromId || !toId) continue;
      const meta = edgeMetadata[fromId]?.[toId] || {};
      edges.push({
        fromNodeId: fromId,
        toNodeId: toId,
        outputSpec: (meta.output_spec || legacy.output_spec || '').trim(),
      });
    }
  } else {
    for (const [fromId, toIds] of Object.entries(edgesRaw || {})) {
      if (Array.isArray(toIds)) {
        for (const toId of toIds) {
          if (typeof toId !== 'string' || !toId) continue;
          const meta = edgeMetadata[fromId]?.[toId] || {};
          edges.push({
            fromNodeId: fromId,
            toNodeId: toId,
            outputSpec: (meta.output_spec || '').trim(),
          });
        }
      } else if (toIds && typeof toIds === 'object') {
        const legacy = toIds as Record<string, any>;
        const toId = legacy.to_node_id || '';
        if (!toId) continue;
        const meta = edgeMetadata[fromId]?.[toId] || {};
        edges.push({
          fromNodeId: legacy.from_node_id || fromId,
          toNodeId: toId,
          outputSpec: (meta.output_spec || legacy.output_spec || '').trim(),
        });
      }
    }
  }

  let myNodeId: string | null = null;
  let myNode: Record<string, any> | null = null;
  for (const [nid, n] of Object.entries(nodes)) {
    if ((n as any)?.workspace_id === workspaceId) {
      myNodeId = nid;
      myNode = n as Record<string, any>;
      break;
    }
  }
  if (!myNode || !myNodeId) return null;

  const incoming: IncomingEdgeSpec[] = [];
  for (const e of edges) {
    if (e.toNodeId === myNodeId) {
      const fromId = e.fromNodeId;
      const src = (nodes[fromId] || {}) as Record<string, any>;
      incoming.push({
        from_node_id: fromId,
        from_label: src.label || src.name || '',
        output_spec: e.outputSpec,
      });
    }
  }

  return {
    label: (myNode.deliverable || '').trim(),
    title: (myNode.label || myNode.name || '').trim(),
    content: (myNode.content || '').trim(),
    incoming_specs: incoming,
  };
}
